import { useState } from 'react';
import { Heart, ShareNetwork, MapPin, Check, ShieldCheck, Clock, PawPrint, Cigarette, FileText } from '@phosphor-icons/react';
import type { HotelDetailDTO, PropertyRatingSummary, PublicReview } from '@pms/shared';
import { StarRating } from '../landing/StarRating';
import { humanize } from '../../lib/format';

export function HotelHeader({
  hotel,
  summary,
}: {
  hotel: HotelDetailDTO;
  /** Real aggregate (TASK-43); falls back to the DTO when not yet loaded. */
  summary?: PropertyRatingSummary | undefined;
}) {
  const [saved, setSaved] = useState(false);
  const [shared, setShared] = useState(false);

  const hasReviews = !!summary && summary.reviewCount > 0;
  const rating = hasReviews ? summary!.averageRating : hotel.rating;
  const reviewCount = hasReviews ? summary!.reviewCount : 0;

  function share() {
    const url = window.location.href;
    navigator.clipboard?.writeText(url).then(() => {
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    });
  }

  return (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="font-serif text-3xl font-black tracking-tight text-ink sm:text-4xl">{hotel.name}</h1>
        <p className="mt-2 flex items-center gap-1.5 text-ink-muted">
          <MapPin className="h-4 w-4 text-clay" /> {hotel.location}
        </p>
        <div className="mt-3">
          {reviewCount > 0 ? (
            <StarRating rating={rating} reviewCount={reviewCount} />
          ) : (
            <span className="inline-flex items-center gap-2">
              <StarRating rating={rating} />
              <span className="rounded-full bg-sand px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-ink-soft">
                New
              </span>
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" aria-pressed={saved} onClick={() => setSaved((s) => !s)}
          className="flex items-center gap-1.5 rounded-full border border-sand px-4 py-2 text-sm font-semibold text-ink transition hover:bg-cream-deep">
          <Heart weight={saved ? 'fill' : 'light'} className={`h-4 w-4 ${saved ? 'text-clay' : 'text-ink-muted'}`} />
          {saved ? 'Saved' : 'Save'}
        </button>
        <button type="button" onClick={share}
          className="flex items-center gap-1.5 rounded-full border border-sand px-4 py-2 text-sm font-semibold text-ink transition hover:bg-cream-deep">
          <ShareNetwork className="h-4 w-4 text-ink-muted" /> {shared ? 'Link copied' : 'Share'}
        </button>
      </div>
    </header>
  );
}

export function AmenityList({ amenities }: { amenities: string[] }) {
  if (!amenities.length) return null;
  return (
    <section aria-labelledby="amenities-h" className="border-t border-sand pt-8">
      <h2 id="amenities-h" className="font-serif text-2xl font-bold text-ink">Amenities</h2>
      <ul className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {amenities.map((a) => (
          <li key={a} className="flex items-center gap-2.5 text-sm text-ink-soft">
            <Check className="h-4 w-4 shrink-0 text-clay" />
            <span>{humanize(a)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function PolicyBlock({ hotel }: { hotel: HotelDetailDTO }) {
  const p = hotel.policies;
  const rows = [
    { icon: ShieldCheck, label: 'Confirmation', value: 'Instant — confirmed with the hotel' },
    { icon: FileText, label: 'Cancellation', value: cap(p.cancellationPolicy) },
    { icon: Clock, label: 'Check-in / out', value: `From ${p.checkInTime} · until ${p.checkOutTime}` },
    { icon: PawPrint, label: 'Pets', value: humanize(p.petPolicy) },
    { icon: Cigarette, label: 'Smoking', value: humanize(p.smokingPolicy) },
  ];
  return (
    <section aria-labelledby="policy-h" className="border-t border-sand pt-8">
      <h2 id="policy-h" className="font-serif text-2xl font-bold text-ink">Good to know</h2>
      <dl className="mt-5 space-y-4">
        {rows.map((r) => (
          <div key={r.label} className="flex items-start gap-3">
            <r.icon className="mt-0.5 h-5 w-5 shrink-0 text-clay" />
            <div>
              <dt className="text-sm font-semibold text-ink">{r.label}</dt>
              <dd className="text-sm text-ink-soft">{r.value}</dd>
            </div>
          </div>
        ))}
        {p.houseRules && (
          <div className="flex items-start gap-3">
            <FileText className="mt-0.5 h-5 w-5 shrink-0 text-clay" />
            <div>
              <dt className="text-sm font-semibold text-ink">House rules</dt>
              <dd className="whitespace-pre-line text-sm text-ink-soft">{p.houseRules}</dd>
            </div>
          </div>
        )}
      </dl>
    </section>
  );
}

export function formatReviewDate(iso: string): string {
  try {
    const d = new Date(iso);
    // A malformed timestamp yields an Invalid Date whose toLocaleDateString
    // returns the literal "Invalid Date" rather than throwing — guard it.
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

/**
 * Guest reviews (TASK-43) — REAL aggregate + approved review list.
 * `summary`/`reviews` come from GET /public/properties/:id/rating and /reviews.
 */
export function ReviewsSection({
  summary,
  reviews,
  isLoading,
}: {
  summary: PropertyRatingSummary | undefined;
  reviews: PublicReview[] | undefined;
  isLoading: boolean;
}) {
  const reviewCount = summary?.reviewCount ?? 0;
  const rating = summary?.averageRating ?? 0;

  return (
    <section aria-labelledby="reviews-h" className="border-t border-sand pt-8">
      <h2 id="reviews-h" className="font-serif text-2xl font-bold text-ink">Guest reviews</h2>

      {isLoading ? (
        <div className="mt-5 space-y-3" aria-hidden="true">
          <div className="h-16 w-40 animate-pulse rounded-xl bg-cream-deep" />
          <div className="h-20 w-full animate-pulse rounded-xl bg-cream-deep" />
        </div>
      ) : reviewCount === 0 ? (
        <p className="mt-4 inline-flex items-center gap-2 rounded-xl bg-sand px-4 py-2.5 text-sm font-medium text-ink-soft">
          Be the first to review this hotel after your stay.
        </p>
      ) : (
        <>
          <div className="mt-5 flex items-center gap-5">
            <div className="text-center">
              <div className="font-serif text-5xl font-black text-ink">{rating.toFixed(1)}</div>
              <StarRating rating={rating} />
            </div>
            <p className="text-sm text-ink-muted">
              Based on <strong className="text-ink">{reviewCount}</strong> verified guest{' '}
              {reviewCount === 1 ? 'review' : 'reviews'}.
            </p>
          </div>

          {reviews && reviews.length > 0 && (
            <ul className="mt-6 grid gap-4 sm:grid-cols-2">
              {reviews.map((r) => (
                <li key={r.id} className="rounded-[1.25rem] border border-sand bg-white p-6">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <StarRating rating={r.rating} compact />
                      {r.title && <span className="font-serif font-semibold text-ink">{r.title}</span>}
                    </div>
                    <span className="text-xs text-ink-muted">{formatReviewDate(r.createdAt)}</span>
                  </div>
                  {r.body && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink-soft">{r.body}</p>}
                  <p className="mt-3 text-xs font-semibold text-ink-muted">— {r.guestName}</p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

export function HotelDetailSkeleton() {
  return (
    <div className="animate-pulse space-y-6" aria-hidden="true">
      <div className="aspect-[16/9] w-full rounded-2xl bg-cream-deep" />
      <div className="h-8 w-1/2 rounded bg-cream-deep" />
      <div className="h-4 w-1/3 rounded bg-cream-deep" />
      <div className="space-y-2">
        <div className="h-4 w-full rounded bg-cream-deep" />
        <div className="h-4 w-5/6 rounded bg-cream-deep" />
      </div>
      <span className="sr-only">Loading hotel…</span>
    </div>
  );
}

function cap(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
